import { loadStripe } from '@stripe/stripe-js';
import { useAppState, useAppDispatch } from '../../Shared/AppContext';

export function useAccountState() {
  return useAppState();
}

export function useAccountActions() {
  const dispatch = useAppDispatch();

  function initializeState({ stripePublishableKey } = {}) {
    const key = stripePublishableKey || null;
    dispatch((appState) => ({
      ...appState,
      loading: true,
      stripePublishableKey: key,
      stripePromise: key ? loadStripe(key) : null,
      account: {
        primaryEmail: null,
      },
      emailUpdateModalIsOpen: false,
      passwordUpdateModalIsOpen: false,
      accountCloseModalIsOpen: false,
      cardFormIsOpen: false,
      cardFormProcessing: false,
      clientSecret: null,
      subscriptionProcessing: false,
      usageType: 'personal',
      emailUpdateForm: {
        email: '',
        password: '',
      },
      passwordUpdateForm: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      },
      closeAccountForm: {
        password: '',
        explicitAcknowledgementPhrase: '',
      },
      paymentMethod: null,
      subscription: null,
      feedbackMessage: null,
      feedbackVariant: null,
    }));
  }

  function onResponse(response) {
    dispatch((appState) => ({ ...appState, ...response }));
  }

  function onError(feedback) {
    dispatch((appState) => ({ ...appState, ...feedback }));
  }

  function openEmailUpdateModal() {
    dispatch((appState) => ({
      ...appState,
      emailUpdateModalIsOpen: true,
      emailUpdateForm: {
        email: appState.account?.primaryEmail || '',
        password: '',
      },
    }));
  }

  function closeEmailUpdateModal() {
    dispatch((appState) => ({
      ...appState,
      emailUpdateModalIsOpen: false,
      emailUpdateForm: {
        email: '',
        password: '',
      },
    }));
  }

  function openPasswordUpdateModal() {
    dispatch((appState) => ({
      ...appState,
      passwordUpdateModalIsOpen: true,
      passwordUpdateForm: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      },
    }));
  }

  function closePasswordUpdateModal() {
    dispatch((appState) => ({
      ...appState,
      passwordUpdateModalIsOpen: false,
      passwordUpdateForm: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      },
    }));
  }

  function openAccountCloseModal() {
    dispatch((appState) => ({
      ...appState,
      accountCloseModalIsOpen: true,
      closeAccountForm: {
        password: '',
        explicitAcknowledgementPhrase: '',
      },
    }));
  }

  function closeAccountCloseModal() {
    dispatch((appState) => ({
      ...appState,
      accountCloseModalIsOpen: false,
      closeAccountForm: {
        password: '',
        explicitAcknowledgementPhrase: '',
      },
    }));
  }

  function openCardForm() {
    dispatch((appState) => ({
      ...appState,
      cardFormIsOpen: true,
      cardFormProcessing: false,
      clientSecret: null,
    }));
  }

  function closeCardForm() {
    dispatch((appState) => ({
      ...appState,
      cardFormIsOpen: false,
      cardFormProcessing: false,
      clientSecret: null,
    }));
  }

  function setCardFormProcessing(processing) {
    dispatch((appState) => ({
      ...appState,
      cardFormProcessing: processing,
    }));
  }

  function setSubscriptionProcessing(processing) {
    dispatch((appState) => ({
      ...appState,
      subscriptionProcessing: processing,
    }));
  }

  function setUsageType(usageType) {
    dispatch((appState) => ({
      ...appState,
      usageType: usageType,
    }));
  }

  function updateEmailFormEmail(email) {
    dispatch((appState) => ({
      ...appState,
      emailUpdateForm: {
        ...appState.emailUpdateForm,
        email: email,
      },
    }));
  }

  function updateEmailFormPassword(password) {
    dispatch((appState) => ({
      ...appState,
      emailUpdateForm: {
        ...appState.emailUpdateForm,
        password: password,
      },
    }));
  }

  function updatePasswordFormCurrentPassword(currentPassword) {
    dispatch((appState) => ({
      ...appState,
      passwordUpdateForm: {
        ...appState.passwordUpdateForm,
        currentPassword: currentPassword,
      },
    }));
  }

  function updatePasswordFormNewPassword(newPassword) {
    dispatch((appState) => ({
      ...appState,
      passwordUpdateForm: {
        ...appState.passwordUpdateForm,
        newPassword: newPassword,
      },
    }));
  }

  function updatePasswordFormConfirmPassword(confirmPassword) {
    dispatch((appState) => ({
      ...appState,
      passwordUpdateForm: {
        ...appState.passwordUpdateForm,
        confirmPassword: confirmPassword,
      },
    }));
  }

  function updateCloseAccountFormPassword(password) {
    dispatch((appState) => ({
      ...appState,
      closeAccountForm: {
        ...appState.closeAccountForm,
        password: password,
      },
    }));
  }

  function updateCloseAccountFormExplicitAcknowledgementPhrase(explicitAcknowledgementPhrase) {
    dispatch((appState) => ({
      ...appState,
      closeAccountForm: {
        ...appState.closeAccountForm,
        explicitAcknowledgementPhrase: explicitAcknowledgementPhrase,
      },
    }));
  }

  return {
    initializeState,
    onResponse,
    onError,
    openEmailUpdateModal,
    closeEmailUpdateModal,
    openPasswordUpdateModal,
    closePasswordUpdateModal,
    openAccountCloseModal,
    closeAccountCloseModal,
    updateEmailFormEmail,
    updateEmailFormPassword,
    updatePasswordFormCurrentPassword,
    updatePasswordFormNewPassword,
    updatePasswordFormConfirmPassword,
    updateCloseAccountFormPassword,
    updateCloseAccountFormExplicitAcknowledgementPhrase,
    openCardForm,
    closeCardForm,
    setCardFormProcessing,
    setSubscriptionProcessing,
    setUsageType,
  };
}
